import { useState, useEffect } from "react";
import { useAuthState, useUpdateUserProfile, useAuthicatedUser, extractErrorMessages } from "react-redux-django-auth";
import { CheckCircle2, Calendar, User, Mail, Phone, MapPin, AlertCircle, ExternalLink } from "lucide-react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import { useToast } from "../../../hooks/otherHooks/useToast";

const GitHubIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
  </svg>
);

const formatDateForInput = (d?: string | null) => {
  if (!d) return "";
  if (typeof d === "string") {
    const trimmed = d.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  try {
    const parsed = new Date(d as string);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }
  } catch {}
  return "";
};

const ProfileInformation = () => {
  const { user: rawUser } = useAuthState();
  const { refreshUser } = useAuthicatedUser();
  const { addToast } = useToast();
  const currentUser = (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;

  const {
    loading,
    formData,
    setFormData,
    onChange,
    updateProfile,
  } = useUpdateUserProfile({
    first_name: currentUser?.first_name || "",
    last_name: currentUser?.last_name || "",
    phone_number: currentUser?.phone_number || "",
    date_of_birth: formatDateForInput(currentUser?.date_of_birth),
    gender: currentUser?.gender || "Male",
    home_address: currentUser?.home_address || "",
    github_username: currentUser?.github_username || "",
  });

  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (currentUser) {
      setFormData((prev: any) => ({
        ...prev,
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        phone_number: currentUser.phone_number || "",
        date_of_birth: formatDateForInput(currentUser.date_of_birth),
        gender: currentUser.gender || "Male",
        home_address: currentUser.home_address || "",
        github_username: currentUser.github_username || "",
      }));
    }
  }, [currentUser, setFormData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);
    try {
      const sanitizedPayload = {
        ...formData,
        date_of_birth: formData?.date_of_birth
          ? formatDateForInput(formData.date_of_birth) || null
          : null,
        github_username: formData?.github_username ? String(formData.github_username).trim() : null,
      };

      await updateProfile(sanitizedPayload);
      if (refreshUser) {
        await refreshUser();
      }

      const successMsg = "Profile information updated successfully.";
      if (addToast) {
        addToast({
          message: successMsg,
          bg: "success",
        });
      }
      setFeedback({ type: "success", message: successMsg });
    } catch (err: any) {
      console.error("Failed to update profile:", err);
      const msgs = extractErrorMessages ? extractErrorMessages(err?.response?.data || err) : [];
      const errorMsg =
        Array.isArray(msgs) && msgs.length > 0
          ? msgs.join(". ")
          : typeof err === "string"
            ? err
            : err?.response?.data?.detail || err?.message || "Failed to update profile. Please try again.";

      if (addToast) {
        addToast({
          message: errorMsg,
          bg: "danger",
        });
      }
      setFeedback({ type: "error", message: errorMsg });
    }
  };

  const handleDiscard = () => {
    setFeedback(null);
    setFormData({
      first_name: currentUser?.first_name || "",
      last_name: currentUser?.last_name || "",
      phone_number: currentUser?.phone_number || "",
      date_of_birth: formatDateForInput(currentUser?.date_of_birth),
      gender: currentUser?.gender || "Male",
      home_address: currentUser?.home_address || "",
      github_username: currentUser?.github_username || "",
    });
  };

  return (
    <div className="cht-space-y-10">
      <header>
        <h2 className="cht-text-2xl cht-font-black cht-text-black">Personal Information</h2>
        <p className="cht-text-sm cht-text-gray-400 cht-mt-1">Manage your personal identification, contact details, and account data.</p>
      </header>

      {feedback && (
        <div
          className={`cht-p-4 cht-rounded-2xl cht-text-xs cht-font-bold cht-flex cht-items-center cht-gap-2.5 ${
            feedback.type === "success"
              ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200"
              : "cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200"
          }`}
        >
          {feedback.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.message}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="cht-space-y-8">
        {/* Gender Selection */}
        <div className="cht-flex cht-items-center cht-gap-8">
          <label className="cht-flex cht-items-center cht-gap-3 cht-cursor-pointer group">
            <div className="cht-relative cht-flex cht-items-center cht-justify-center">
              <input 
                type="radio" 
                name="gender" 
                value="Male" 
                checked={formData?.gender === "Male"}
                onChange={onChange}
                className="cht-appearance-none cht-w-5 cht-h-5 cht-border-2 cht-border-gray-200 cht-rounded-full checked:cht-border-[#3e57da] transition-all"
              />
              {formData?.gender === "Male" && <div className="cht-absolute cht-w-2.5 cht-h-2.5 cht-bg-[#3e57da] cht-rounded-full"></div>}
            </div>
            <span className={`cht-text-sm cht-font-bold ${formData?.gender === "Male" ? "cht-text-black" : "cht-text-gray-400"}`}>Male</span>
          </label>
          <label className="cht-flex cht-items-center cht-gap-3 cht-cursor-pointer group">
            <div className="cht-relative cht-flex cht-items-center cht-justify-center">
              <input 
                type="radio" 
                name="gender" 
                value="Female" 
                checked={formData?.gender === "Female"}
                onChange={onChange}
                className="cht-appearance-none cht-w-5 cht-h-5 cht-border-2 cht-border-gray-200 cht-rounded-full checked:cht-border-[#3e57da] transition-all"
              />
              {formData?.gender === "Female" && <div className="cht-absolute cht-w-2.5 cht-h-2.5 cht-bg-[#3e57da] cht-rounded-full"></div>}
            </div>
            <span className={`cht-text-sm cht-font-bold ${formData?.gender === "Female" ? "cht-text-black" : "cht-text-gray-400"}`}>Female</span>
          </label>
        </div>

        {/* Input Grid */}
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-x-8 cht-gap-y-6">
          {/* First Name */}
          <Input 
            label="First Name"
            type="text" 
            name="first_name"
            value={formData?.first_name || ""}
            onChange={onChange}
            leftIcon={<User size={18} />}
            placeholder="First Name"
            required
          />

          {/* Last Name */}
          <Input 
            label="Last Name"
            type="text" 
            name="last_name"
            value={formData?.last_name || ""}
            onChange={onChange}
            leftIcon={<User size={18} />}
            placeholder="Last Name"
            required
          />

          {/* Email */}
          <div className="cht-col-span-full">
            <Input 
              label="Email Address"
              type="email" 
              name="email"
              value={currentUser?.email || ""}
              disabled
              leftIcon={<Mail size={18} />}
              placeholder="email@example.com"
              subLabel="Managed by account credentials"
              rightElement={
                <div className="cht-flex cht-items-center cht-gap-1.5 cht-text-emerald-600 cht-text-xs cht-font-bold">
                  <CheckCircle2 size={16} /> Verified
                </div>
              }
            />
          </div>

          {/* Phone Number */}
          <Input 
            label="Phone Number"
            type="tel" 
            name="phone_number"
            value={formData?.phone_number || ""}
            onChange={onChange}
            leftIcon={<Phone size={18} />}
            placeholder="e.g. +234 800 000 0000"
          />

          {/* Date of Birth */}
          <Input 
            label="Date of Birth"
            type="date" 
            name="date_of_birth"
            value={formData?.date_of_birth || ""}
            onChange={onChange}
            rightIcon={<Calendar size={18} />}
          />

          {/* Address */}
          <div className="cht-col-span-full">
            <Input 
              label="Primary Address"
              type="text" 
              name="home_address"
              value={formData?.home_address || ""}
              onChange={onChange}
              leftIcon={<MapPin size={18} />}
              placeholder="Street address, city, state"
            />
          </div>
        </div>

        {/* Developer Integration: GitHub Account */}
        <div className="cht-p-6 cht-rounded-[24px] cht-bg-slate-50/80 cht-border cht-border-slate-200/80 cht-space-y-4">
          <div className="cht-flex cht-items-start cht-justify-between cht-gap-4">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-slate-900 cht-text-white cht-flex cht-items-center cht-justify-center cht-shrink-0">
                <GitHubIcon className="cht-w-5 cht-h-5" />
              </div>
              <div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  <h4 className="cht-text-sm cht-font-bold cht-text-slate-900">
                    GitHub Account Integration
                  </h4>
                  {currentUser?.github_username ? (
                    <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200">
                      <CheckCircle2 size={11} /> Connected
                    </span>
                  ) : (
                    <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-amber-50 cht-text-amber-800 cht-border cht-border-amber-200">
                      Not Connected
                    </span>
                  )}
                </div>
                <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5 leading-relaxed">
                  Required for automated CI coding assignments. When connected, our system automatically provisions your private assignment repositories and records CI autograder results.
                </p>
              </div>
            </div>

            {currentUser?.github_username && (
              <a
                href={`https://github.com/${currentUser.github_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cht-hidden sm:cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-[#3e57da] hover:cht-underline cht-shrink-0"
              >
                <span>View on GitHub</span>
                <ExternalLink size={13} />
              </a>
            )}
          </div>

          <div className="cht-pt-2">
            <Input
              label="GitHub Username"
              type="text"
              name="github_username"
              value={formData?.github_username || ""}
              onChange={onChange}
              leftIcon={<GitHubIcon className="cht-w-4 cht-h-4 cht-text-slate-500" />}
              placeholder="e.g. octocat (your GitHub handle without @)"
              helperText="Enter your exact GitHub username so the GitHub App can invite you to assignment repositories."
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="cht-flex cht-items-center cht-justify-end cht-gap-4 cht-pt-6">
          <Button 
            type="button"
            variant="outline" 
            onClick={handleDiscard}
            disabled={loading}
            className="cht-border-[#3e57da] cht-text-[#3e57da] hover:cht-bg-[#3e57da]/5"
          >
            Discard Changes
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            isLoading={loading}
            className="cht-bg-[#3e57da] cht-border-none hover:cht-bg-[#2d41a8]"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileInformation;
